import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Plus,
  Save,
  Trash2,
  UserRoundCheck
} from 'lucide-react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../core/firebase/firebase';

const DIFFICULTIES = [
  ['reading', 'Lecture'],
  ['writing', 'Écriture'],
  ['math', 'Mathématiques'],
  ['attention', 'Concentration / attention'],
  ['language', 'Langage / communication'],
  ['behavior', 'Comportement'],
  ['social', 'Socialisation'],
  ['autonomy', 'Autonomie'],
  ['motor', 'Difficultés motrices'],
  ['other', 'Autre']
];

const PREVIOUS_CLASSES = [
  'Préparatoire',
  '1ère année',
  '2ème année',
  '3ème année',
  '4ème année',
  '5ème année',
  '6ème année'
];

const EMERGENCY_RELATIONSHIPS = [
  'Tante',
  'Oncle',
  'Amie de la famille',
  'Grand-père',
  'Grand-mère'
];

function parseBirthDate(value = '') {
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? { year: match[1], month: match[2], day: match[3] } : { year: '', month: '', day: '' };
}

function buildBirthDate(day, month, year) {
  if (!day || !month || !year) return '';
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function validBirthDate(day, month, year) {
  const iso = buildBirthDate(day, month, year);
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d && y >= 1900 && y <= new Date().getFullYear();
}

function displayBirthDate(child) {
  if (!child?.birthDay || !child?.birthMonth || !child?.birthYear) return '—';
  return `${String(child.birthDay).padStart(2, '0')}/${String(child.birthMonth).padStart(2, '0')}/${child.birthYear}`;
}

function choiceWithOther(value, preferred) {
  const current = String(value || '').trim();
  if (!current) return { choice: '', other: '' };
  if (current === preferred) return { choice: preferred, other: '' };
  return { choice: 'Autre', other: current };
}

function normalizeEmergencyContact(source = {}) {
  const relationship = String(source.relationship || '').trim();
  const isKnown = EMERGENCY_RELATIONSHIPS.includes(relationship);
  return {
    fullName: source.fullName || '',
    relationship: relationship ? (isKnown ? relationship : 'Autre') : '',
    relationshipOther: relationship && !isKnown ? relationship : '',
    phone: source.phone || ''
  };
}

const emptyGuardian = (relationship = '') => ({
  relationship,
  fullName: '',
  cin: '',
  profession: '',
  phone: '',
  secondaryPhone: '',
  email: '',
  address: '',
  sameAddressAsChild: false
});

function normalizeGuardian(source = {}, fallbackRelationship = '') {
  return {
    relationship: source.relationship || fallbackRelationship,
    fullName: source.fullName || '',
    cin: source.cin || '',
    profession: source.profession || '',
    phone: source.phone || '',
    secondaryPhone: source.secondaryPhone || '',
    email: source.email || '',
    address: source.address || '',
    sameAddressAsChild: Boolean(source.sameAddressAsChild)
  };
}

const emptySibling = () => ({
  id: crypto.randomUUID?.() || `sibling_${Date.now()}_${Math.random()}`,
  fullName: '',
  className: '',
  school: 'Le Défi'
});

const emptyPickup = () => ({
  id: crypto.randomUUID?.() || `pickup_${Date.now()}_${Math.random()}`,
  fullName: '',
  relationship: '',
  phone: ''
});

const clean = (value) => String(value || '').trim();

function childLabel(child) {
  return child?.fullName || child?.fullname || child?.studentFullName || child?.name || 'Élève';
}

function buildChildProfile(child, saved = {}) {
  const savedBirthDate = saved.birthDate ?? child.birthDate ?? '';
  const birth = parseBirthDate(savedBirthDate);
  const birthPlace = choiceWithOther(saved.birthPlace ?? '', 'Bizerte');
  const nationality = choiceWithOther(saved.nationality ?? 'Tunisienne', 'Tunisienne');
  const previousSchool = choiceWithOther(saved.previousSchool ?? child.previousSchool ?? '', 'Le Défi');

  return {
    studentId: child.id,
    fullName: childLabel(child),
    className: child.className || child.level || child.requestedLevel || '',
    schoolYear: child.schoolYear || '',
    birthDate: savedBirthDate,
    birthDay: birth.day,
    birthMonth: birth.month,
    birthYear: birth.year,
    birthPlace: birthPlace.choice,
    birthPlaceOther: birthPlace.other,
    sex: saved.sex ?? '',
    nationality: nationality.choice || 'Tunisienne',
    nationalityOther: nationality.other,
    address: saved.address ?? '',
    livesWith: saved.livesWith ?? '',
    livesWithOther: saved.livesWithOther ?? '',
    previousSchool: previousSchool.choice,
    previousSchoolOther: previousSchool.other,
    previousClass: saved.previousClass ?? '',
    repeatedClass: saved.repeatedClass ?? false,
    repeatedClassName: saved.repeatedClassName ?? '',
    hasSiblings: saved.hasSiblings ?? false,
    siblingAtSchool: saved.siblingAtSchool ?? false,
    siblings: Array.isArray(saved.siblings) ? saved.siblings : [],
    difficulties: Array.isArray(saved.difficulties) ? saved.difficulties : [],
    difficultyDetails: saved.difficultyDetails ?? child.difficulties ?? '',
    pedagogicalNotes: saved.pedagogicalNotes ?? '',
    allergies: saved.allergies ?? false,
    allergyDetails: saved.allergyDetails ?? '',
    treatmentOrPrecaution: saved.treatmentOrPrecaution ?? false,
    treatmentDetails: saved.treatmentDetails ?? '',
    emergencyHealthNotes: saved.emergencyHealthNotes ?? ''
  };
}

function buildInitialProfile(parent, children) {
  const saved = parent?.onboardingProfile || {};
  const guardian1 = normalizeGuardian({
    relationship: parent?.relationship || 'mother',
    fullName: parent?.fullName || parent?.fullname || '',
    cin: parent?.cin || '',
    profession: parent?.profession || '',
    phone: parent?.phone || '',
    email: parent?.email || '',
    address: parent?.address || '',
    ...(saved.guardian1 || {})
  }, parent?.relationship || 'mother');

  const childSaved = new Map(
    (Array.isArray(saved.children) ? saved.children : []).map((item) => [item.studentId, item])
  );

  return {
    schemaVersion: 1,
    familyStatus: saved.familyStatus || '',
    childResidence: saved.childResidence || '',
    childResidenceOther: saved.childResidenceOther || '',
    custodyType: saved.custodyType || '',
    parentalAuthority: saved.parentalAuthority || '',
    courtDecision: saved.courtDecision ?? false,
    preferredContact: saved.preferredContact || 'both',
    guardian1,
    guardian2: normalizeGuardian(saved.guardian2 || {}, 'father'),
    children: children.map((child) => buildChildProfile(child, childSaved.get(child.id))),
    emergencyContact: normalizeEmergencyContact(saved.emergencyContact || {}),
    legalGuardiansOnlyPickup: saved.legalGuardiansOnlyPickup ?? true,
    authorizedPickup: Array.isArray(saved.authorizedPickup) ? saved.authorizedPickup : [],
    confirmationAccepted: false
  };
}

function relationshipLabel(value) {
  return ({ mother: 'Mère', father: 'Père', guardian: 'Tuteur légal', other: 'Autre' })[value] || value || '—';
}

function familyStatusLabel(value) {
  return ({ married: 'Mariés', divorced: 'Divorcés', separated: 'Séparés', widowed: 'Veuf / veuve', other: 'Autre' })[value] || value || '—';
}

function yesNo(value) { return value ? 'Oui' : 'Non'; }

function TextField({ label, value, onChange, type = 'text', required = false, placeholder = '', disabled = false }) {
  return <label>{label}<input type={type} value={value || ''} onChange={(event) => onChange(event.target.value)} required={required} placeholder={placeholder} disabled={disabled}/></label>;
}

function SelectField({ label, value, onChange, children, required = false, disabled = false }) {
  return <label>{label}<select value={value || ''} onChange={(event) => onChange(event.target.value)} required={required} disabled={disabled}>{children}</select></label>;
}

function DateOfBirthField({ child, onChange }) {
  const days = Array.from({ length: 31 }, (_, index) => String(index + 1).padStart(2, '0'));
  const months = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'));

  return <label>Date de naissance
    <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 0.9fr 1.2fr', gap: 8 }}>
      <select aria-label="Jour de naissance" value={child.birthDay || ''} onChange={(event) => onChange('birthDay', event.target.value)} required>
        <option value="">JJ</option>{days.map((day) => <option key={day} value={day}>{day}</option>)}
      </select>
      <select aria-label="Mois de naissance" value={child.birthMonth || ''} onChange={(event) => onChange('birthMonth', event.target.value)} required>
        <option value="">MM</option>{months.map((month) => <option key={month} value={month}>{month}</option>)}
      </select>
      <input aria-label="Année de naissance" type="number" inputMode="numeric" min="1900" max={new Date().getFullYear()} placeholder="AAAA" value={child.birthYear || ''} onChange={(event) => onChange('birthYear', event.target.value.replace(/\D/g, '').slice(0, 4))} required/>
    </div>
  </label>;
}

function GuardianEditor({ title, value, onChange, required = false }) {
  const patch = (field, fieldValue) => onChange({ ...value, [field]: fieldValue });
  return <article className="parent-onboarding-subcard">
    <div className="parent-onboarding-subcard-title"><strong>{title}</strong><span>{relationshipLabel(value.relationship)}</span></div>
    <div className="parent-onboarding-grid two">
      <SelectField label="Lien avec l’enfant" value={value.relationship} onChange={(v) => patch('relationship', v)} required={required}>
        <option value="">Choisir</option><option value="mother">Mère</option><option value="father">Père</option><option value="guardian">Tuteur légal</option><option value="other">Autre</option>
      </SelectField>
      <TextField label="Nom et prénom" value={value.fullName} onChange={(v) => patch('fullName', v)} required={required}/>
      <TextField label="CIN" value={value.cin} onChange={(v) => patch('cin', v)} required={required}/>
      <TextField label="Profession / fonction" value={value.profession} onChange={(v) => patch('profession', v)} required={required}/>
      <TextField label="Téléphone principal" value={value.phone} onChange={(v) => patch('phone', v)} required={required}/>
      <TextField label="Deuxième téléphone" value={value.secondaryPhone} onChange={(v) => patch('secondaryPhone', v)}/>
      <TextField label="Adresse e-mail" type="email" value={value.email} onChange={(v) => patch('email', v)}/>
      <TextField label="Adresse complète et détaillée" value={value.address} onChange={(v) => patch('address', v)} required={required} placeholder="N°, rue, quartier, ville…"/>
    </div>
  </article>;
}

export default function ParentProfileForm({ parent, children, user, onboarding = false, onSaved, onCancel }) {
  const [form, setForm] = useState(() => buildInitialProfile(parent, children));
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const steps = useMemo(() => [
    'Enfant',
    'Famille',
    'Responsables',
    'Scolarité',
    'Santé & urgence',
    'Vérification'
  ], []);

  const patch = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateChild = (studentId, field, value) => setForm((current) => ({
    ...current,
    children: current.children.map((child) => child.studentId === studentId ? { ...child, [field]: value } : child)
  }));

  function validateStep(index) {
    setError('');
    if (index === 0) {
      if (!form.children.length) {
        setError('Aucun enfant n’est relié à ce compte. Contactez l’administration.');
        return false;
      }
      if (form.children.some((child) => !validBirthDate(child.birthDay, child.birthMonth, child.birthYear) || !clean(child.birthPlace) || !clean(child.address) || !clean(child.livesWith))) {
        setError('Complétez une date de naissance valide, le lieu de naissance, l’adresse complète et le lieu de vie de chaque enfant.');
        return false;
      }
      if (form.children.some((child) => child.birthPlace === 'Autre' && !clean(child.birthPlaceOther))) {
        setError('Précisez le lieu de naissance lorsque vous choisissez « Autre ».');
        return false;
      }
      if (form.children.some((child) => child.nationality === 'Autre' && !clean(child.nationalityOther))) {
        setError('Précisez la nationalité lorsque vous choisissez « Autre ».');
        return false;
      }
    }
    if (index === 1 && !form.familyStatus) {
      setError('Indiquez la situation familiale.');
      return false;
    }
    if (index === 2) {
      const g = form.guardian1;
      if (!clean(g.relationship) || !clean(g.fullName) || !clean(g.cin) || !clean(g.profession) || !clean(g.phone) || !clean(g.address)) {
        setError('Complétez les informations obligatoires du responsable légal 1.');
        return false;
      }
      const g2 = form.guardian2;
      const secondStarted = Object.entries(g2).some(([key, value]) => key !== 'relationship' && key !== 'sameAddressAsChild' && clean(value));
      if (secondStarted && (!clean(g2.relationship) || !clean(g2.fullName) || !clean(g2.cin) || !clean(g2.phone))) {
        setError('Le responsable légal 2 est commencé : complétez au minimum le lien, le nom, la CIN et le téléphone.');
        return false;
      }
    }
    if (index === 4) {
      const e = form.emergencyContact;
      const emergencyStarted = [e.fullName, e.relationship, e.relationshipOther, e.phone].some((value) => clean(value));
      if (emergencyStarted && (!clean(e.fullName) || !clean(e.relationship) || !clean(e.phone) || (e.relationship === 'Autre' && !clean(e.relationshipOther)))) {
        setError('Le contact d’urgence est facultatif. Si vous commencez à le renseigner, complétez le nom, le lien avec l’enfant et le téléphone.');
        return false;
      }
    }
    return true;
  }

  function next() {
    if (validateStep(step)) setStep((current) => Math.min(current + 1, steps.length - 1));
  }

  async function save() {
    for (const index of [0, 1, 2, 4]) {
      if (!validateStep(index)) {
        setStep(index);
        return;
      }
    }
    if (onboarding && !form.confirmationAccepted) {
      setError('Vous devez confirmer que les informations sont exactes et à jour.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        guardian1: normalizeGuardian(Object.fromEntries(Object.entries(form.guardian1).map(([key, value]) => [key, typeof value === 'string' ? clean(value) : value]))),
        guardian2: normalizeGuardian(Object.fromEntries(Object.entries(form.guardian2).map(([key, value]) => [key, typeof value === 'string' ? clean(value) : value]))),
        children: form.children.map((child) => {
          const {
            birthDay,
            birthMonth,
            birthYear,
            birthPlaceOther,
            nationalityOther,
            previousSchoolOther,
            ...baseChild
          } = child;
          return {
            ...baseChild,
            fullName: clean(child.fullName),
            className: clean(child.className),
            birthDate: buildBirthDate(birthDay, birthMonth, birthYear),
            birthPlace: clean(child.birthPlace === 'Autre' ? birthPlaceOther : child.birthPlace),
            nationality: clean(child.nationality === 'Autre' ? nationalityOther : child.nationality),
            address: clean(child.address),
            previousSchool: clean(child.previousSchool === 'Autre' ? previousSchoolOther : child.previousSchool),
            difficultyDetails: clean(child.difficultyDetails),
            pedagogicalNotes: clean(child.pedagogicalNotes),
            allergyDetails: clean(child.allergyDetails),
            treatmentDetails: clean(child.treatmentDetails),
            emergencyHealthNotes: clean(child.emergencyHealthNotes),
            siblings: child.siblings.map((sibling) => ({ ...sibling, fullName: clean(sibling.fullName), className: clean(sibling.className) }))
          };
        }),
        emergencyContact: {
          fullName: clean(form.emergencyContact.fullName),
          relationship: clean(form.emergencyContact.relationship === 'Autre' ? form.emergencyContact.relationshipOther : form.emergencyContact.relationship),
          phone: clean(form.emergencyContact.phone)
        },
        authorizedPickup: form.authorizedPickup.map((person) => ({
          ...person,
          fullName: clean(person.fullName), relationship: clean(person.relationship), phone: clean(person.phone)
        })),
        confirmationAccepted: true
      };

      const primary = payload.guardian1;
      await updateDoc(doc(db, 'parents', parent.id), {
        fullName: primary.fullName,
        fullname: primary.fullName,
        phone: primary.phone,
        normalizedPhone: primary.phone.replace(/\D/g, ''),
        email: primary.email,
        cin: primary.cin,
        profession: primary.profession,
        relationship: primary.relationship,
        address: primary.address,
        onboardingProfile: payload,
        onboardingUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      if (onboarding) {
        await updateDoc(doc(db, 'users', user.uid), {
          onboardingCompleted: true,
          parentOnboardingVersion: 1,
          onboardingCompletedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      onSaved?.(payload);
    } catch (err) {
      console.error('ParentProfileForm — save:', err);
      setError('Impossible d’enregistrer les informations. Vérifiez votre connexion et réessayez.');
    } finally {
      setSaving(false);
    }
  }

  const renderChildrenIdentity = () => <div className="parent-onboarding-stack">
    {form.children.map((child) => <article className="parent-onboarding-subcard" key={child.studentId}>
      <div className="parent-onboarding-subcard-title"><strong>{child.fullName}</strong><span>{child.className || 'Classe à confirmer'}</span></div>
      <div className="parent-readonly-row"><span>Nom et prénom</span><strong>{child.fullName}</strong><small>Information gérée par l’administration</small></div>
      <div className="parent-readonly-row"><span>Classe</span><strong>{child.className || 'À confirmer'}</strong><small>Information gérée par l’administration</small></div>
      <div className="parent-onboarding-grid two">
        <DateOfBirthField child={child} onChange={(field, value) => updateChild(child.studentId, field, value)}/>
        <div>
          <SelectField label="Lieu de naissance" value={child.birthPlace} onChange={(v) => updateChild(child.studentId, 'birthPlace', v)} required>
            <option value="">Choisir</option><option value="Bizerte">Bizerte</option><option value="Autre">Autre</option>
          </SelectField>
          {child.birthPlace === 'Autre' && <TextField label="Préciser le lieu de naissance" value={child.birthPlaceOther} onChange={(v) => updateChild(child.studentId, 'birthPlaceOther', v)} required/>}
        </div>
        <SelectField label="Sexe" value={child.sex} onChange={(v) => updateChild(child.studentId, 'sex', v)}><option value="">Choisir</option><option value="female">Fille</option><option value="male">Garçon</option></SelectField>
        <div>
          <SelectField label="Nationalité" value={child.nationality} onChange={(v) => updateChild(child.studentId, 'nationality', v)}>
            <option value="Tunisienne">Tunisienne</option><option value="Autre">Autre</option>
          </SelectField>
          {child.nationality === 'Autre' && <TextField label="Préciser la nationalité" value={child.nationalityOther} onChange={(v) => updateChild(child.studentId, 'nationalityOther', v)} required/>}
        </div>
        <div className="parent-onboarding-field-span"><TextField label="Adresse complète et détaillée de résidence de l’enfant" value={child.address} onChange={(v) => updateChild(child.studentId, 'address', v)} required placeholder="N°, rue, quartier, ville…"/></div>
        <SelectField label="L’enfant vit principalement" value={child.livesWith} onChange={(v) => updateChild(child.studentId, 'livesWith', v)} required>
          <option value="">Choisir</option><option value="both">Avec ses deux parents</option><option value="mother">Avec sa mère</option><option value="father">Avec son père</option><option value="alternating">En garde alternée</option><option value="guardian">Avec un tuteur / autre personne</option>
        </SelectField>
        {child.livesWith === 'guardian' && <TextField label="Préciser" value={child.livesWithOther} onChange={(v) => updateChild(child.studentId, 'livesWithOther', v)}/>}
      </div>
    </article>)}
  </div>;

  const renderFamily = () => <div className="parent-onboarding-stack">
    <div className="parent-onboarding-grid two">
      <div className="parent-onboarding-field-span">
        <SelectField label="Situation familiale des parents" value={form.familyStatus} onChange={(v) => patch('familyStatus', v)} required>
          <option value="">Choisir</option><option value="married">Mariés</option><option value="divorced">Divorcés</option><option value="separated">Séparés</option><option value="widowed">Veuf / veuve</option><option value="other">Autre</option>
        </SelectField>
      </div>
    </div>
    {['divorced', 'separated'].includes(form.familyStatus) && <article className="parent-onboarding-subcard">
      <strong>Organisation de la garde</strong>
      <div className="parent-onboarding-grid two">
        <SelectField label="Résidence principale de l’enfant" value={form.childResidence} onChange={(v) => patch('childResidence', v)}><option value="">Choisir</option><option value="mother">Chez la mère</option><option value="father">Chez le père</option><option value="alternating">Garde alternée</option><option value="guardian">Chez un tuteur / autre</option></SelectField>
        <SelectField label="Autorité parentale" value={form.parentalAuthority} onChange={(v) => patch('parentalAuthority', v)}><option value="">Choisir</option><option value="both">Père et mère</option><option value="mother">Mère</option><option value="father">Père</option><option value="guardian">Tuteur</option></SelectField>
      </div>
      <label className="parent-check-row"><input type="checkbox" checked={form.courtDecision} onChange={(e) => patch('courtDecision', e.target.checked)}/><span>Il existe une décision judiciaire concernant la garde ou la récupération de l’enfant.</span></label>
    </article>}
  </div>;

  const guardianContactLabel = (guardian, fallback) => {
    const relation = guardian?.relationship ? relationshipLabel(guardian.relationship) : fallback;
    const name = clean(guardian?.fullName);
    return name ? `${relation} — ${name}` : relation;
  };

  const preferredContactLabel = () => {
    if (form.preferredContact === 'guardian1') return guardianContactLabel(form.guardian1, 'Responsable légal 1');
    if (form.preferredContact === 'guardian2') return guardianContactLabel(form.guardian2, 'Responsable légal 2');
    return 'Les deux responsables';
  };

  const renderGuardians = () => <div className="parent-onboarding-stack">
    <GuardianEditor title="Responsable légal 1" value={form.guardian1} onChange={(value) => patch('guardian1', value)} required/>
    <GuardianEditor title="Responsable légal 2" value={form.guardian2} onChange={(value) => patch('guardian2', value)}/>
    <article className="parent-onboarding-subcard">
      <strong>Contact parent prioritaire</strong>
      <p>Choisissez la personne que l’école doit contacter en priorité après avoir renseigné les responsables ci-dessus.</p>
      <SelectField label="Qui devons-nous contacter en priorité ?" value={form.preferredContact} onChange={(v) => patch('preferredContact', v)}>
        <option value="both">Les deux responsables</option>
        <option value="guardian1">{guardianContactLabel(form.guardian1, 'Responsable légal 1')}</option>
        <option value="guardian2">{guardianContactLabel(form.guardian2, 'Responsable légal 2')}</option>
      </SelectField>
    </article>
  </div>;

  const renderSchool = () => <div className="parent-onboarding-stack">
    {form.children.map((child) => <article className="parent-onboarding-subcard" key={child.studentId}>
      <div className="parent-onboarding-subcard-title"><strong>{child.fullName}</strong><span>{child.className || 'Classe à confirmer'}</span></div>
      <div className="parent-onboarding-grid two">
        <div>
          <SelectField label="Établissement précédent" value={child.previousSchool} onChange={(v) => updateChild(child.studentId, 'previousSchool', v)}>
            <option value="">Choisir</option><option value="Le Défi">Le Défi</option><option value="Autre">Autre</option>
          </SelectField>
          {child.previousSchool === 'Autre' && <TextField label="Préciser l’établissement" value={child.previousSchoolOther} onChange={(v) => updateChild(child.studentId, 'previousSchoolOther', v)}/>} 
        </div>
        <SelectField label="Classe fréquentée l’année précédente" value={child.previousClass} onChange={(v) => updateChild(child.studentId, 'previousClass', v)}>
          <option value="">Choisir</option>{PREVIOUS_CLASSES.map((className) => <option key={className} value={className}>{className}</option>)}
        </SelectField>
      </div>
      <label className="parent-check-row"><input type="checkbox" checked={child.repeatedClass} onChange={(e) => updateChild(child.studentId, 'repeatedClass', e.target.checked)}/><span>L’enfant a déjà redoublé une classe.</span></label>
      {child.repeatedClass && <TextField label="Classe redoublée" value={child.repeatedClassName} onChange={(v) => updateChild(child.studentId, 'repeatedClassName', v)}/>} 
      <label className="parent-check-row"><input type="checkbox" checked={child.hasSiblings} onChange={(e) => updateChild(child.studentId, 'hasSiblings', e.target.checked)}/><span>L’enfant a des frères ou sœurs.</span></label>
      {child.hasSiblings && <>
        <label className="parent-check-row"><input type="checkbox" checked={child.siblingAtSchool} onChange={(e) => updateChild(child.studentId, 'siblingAtSchool', e.target.checked)}/><span>Un frère ou une sœur est déjà inscrit au Défi.</span></label>
        <div className="parent-repeat-list">
          {child.siblings.map((sibling) => <div className="parent-repeat-row" key={sibling.id}>
            <input placeholder="Nom et prénom" value={sibling.fullName} onChange={(e) => updateChild(child.studentId, 'siblings', child.siblings.map((item) => item.id === sibling.id ? { ...item, fullName: e.target.value } : item))}/>
            <input placeholder="Classe" value={sibling.className} onChange={(e) => updateChild(child.studentId, 'siblings', child.siblings.map((item) => item.id === sibling.id ? { ...item, className: e.target.value } : item))}/>
            <select value={sibling.school} onChange={(e) => updateChild(child.studentId, 'siblings', child.siblings.map((item) => item.id === sibling.id ? { ...item, school: e.target.value } : item))}><option>Le Défi</option><option>Autre établissement</option></select>
            <button type="button" onClick={() => updateChild(child.studentId, 'siblings', child.siblings.filter((item) => item.id !== sibling.id))} aria-label="Supprimer"><Trash2 size={16}/></button>
          </div>)}
          <button type="button" className="button button-secondary parent-inline-button" onClick={() => updateChild(child.studentId, 'siblings', [...child.siblings, emptySibling()])}><Plus size={16}/> Ajouter un frère / une sœur</button>
        </div>
      </>}
      <div className="parent-onboarding-question"><strong>Difficultés ou besoins nécessitant une attention particulière</strong><p>Sélectionnez uniquement les éléments utiles au suivi pédagogique.</p></div>
      <div className="parent-check-grid">
        {DIFFICULTIES.map(([key, label]) => <label key={key}><input type="checkbox" checked={child.difficulties.includes(key)} onChange={(e) => updateChild(child.studentId, 'difficulties', e.target.checked ? [...child.difficulties, key] : child.difficulties.filter((item) => item !== key))}/><span>{label}</span></label>)}
      </div>
      <label>Précisions utiles<textarea value={child.difficultyDetails} onChange={(e) => updateChild(child.studentId, 'difficultyDetails', e.target.value)} placeholder="Facultatif"/></label>
      <label>Informations complémentaires pour l’équipe pédagogique<textarea value={child.pedagogicalNotes} onChange={(e) => updateChild(child.studentId, 'pedagogicalNotes', e.target.value)} placeholder="Facultatif"/></label>
    </article>)}
  </div>;

  const renderHealth = () => <div className="parent-onboarding-stack">
    {form.children.map((child) => <article className="parent-onboarding-subcard" key={child.studentId}>
      <div className="parent-onboarding-subcard-title"><strong>{child.fullName}</strong><span>Santé</span></div>
      <label className="parent-check-row"><input type="checkbox" checked={child.allergies} onChange={(e) => updateChild(child.studentId, 'allergies', e.target.checked)}/><span>Allergies connues</span></label>
      {child.allergies && <label>Préciser les allergies<textarea value={child.allergyDetails} onChange={(e) => updateChild(child.studentId, 'allergyDetails', e.target.value)}/></label>}
      <label className="parent-check-row"><input type="checkbox" checked={child.treatmentOrPrecaution} onChange={(e) => updateChild(child.studentId, 'treatmentOrPrecaution', e.target.checked)}/><span>Traitement ou précaution importante à connaître</span></label>
      {child.treatmentOrPrecaution && <label>Préciser<textarea value={child.treatmentDetails} onChange={(e) => updateChild(child.studentId, 'treatmentDetails', e.target.value)}/></label>}
      <label>Autre information importante en cas d’urgence<textarea value={child.emergencyHealthNotes} onChange={(e) => updateChild(child.studentId, 'emergencyHealthNotes', e.target.value)} placeholder="Facultatif"/></label>
    </article>)}
    <article className="parent-onboarding-subcard">
      <strong>Contact d’urgence si les responsables sont injoignables <small>(facultatif)</small></strong>
      <p>Vous pouvez laisser cette section vide. Si vous ajoutez un contact, renseignez les trois informations.</p>
      <div className="parent-onboarding-grid three">
        <TextField label="Nom et prénom" value={form.emergencyContact.fullName} onChange={(v) => patch('emergencyContact', { ...form.emergencyContact, fullName: v })}/>
        <div>
          <SelectField label="Lien avec l’enfant" value={form.emergencyContact.relationship} onChange={(v) => patch('emergencyContact', { ...form.emergencyContact, relationship: v })}>
            <option value="">Choisir</option>
            {EMERGENCY_RELATIONSHIPS.map((relationship) => <option key={relationship} value={relationship}>{relationship}</option>)}
            <option value="Autre">Autre</option>
          </SelectField>
          {form.emergencyContact.relationship === 'Autre' && <TextField label="Préciser le lien" value={form.emergencyContact.relationshipOther} onChange={(v) => patch('emergencyContact', { ...form.emergencyContact, relationshipOther: v })}/>} 
        </div>
        <TextField label="Téléphone" value={form.emergencyContact.phone} onChange={(v) => patch('emergencyContact', { ...form.emergencyContact, phone: v })}/>
      </div>
    </article>
    <article className="parent-onboarding-subcard">
      <strong>Personnes autorisées à récupérer l’enfant</strong>
      <label className="parent-check-row"><input type="checkbox" checked={form.legalGuardiansOnlyPickup} onChange={(e) => patch('legalGuardiansOnlyPickup', e.target.checked)}/><span>Seuls les responsables légaux peuvent récupérer mon enfant.</span></label>
      {!form.legalGuardiansOnlyPickup && <div className="parent-repeat-list">
        {form.authorizedPickup.map((person) => <div className="parent-repeat-row" key={person.id}>
          <input placeholder="Nom et prénom" value={person.fullName} onChange={(e) => patch('authorizedPickup', form.authorizedPickup.map((item) => item.id === person.id ? { ...item, fullName: e.target.value } : item))}/>
          <input placeholder="Lien avec l’enfant" value={person.relationship} onChange={(e) => patch('authorizedPickup', form.authorizedPickup.map((item) => item.id === person.id ? { ...item, relationship: e.target.value } : item))}/>
          <input placeholder="Téléphone" value={person.phone} onChange={(e) => patch('authorizedPickup', form.authorizedPickup.map((item) => item.id === person.id ? { ...item, phone: e.target.value } : item))}/>
          <button type="button" onClick={() => patch('authorizedPickup', form.authorizedPickup.filter((item) => item.id !== person.id))} aria-label="Supprimer"><Trash2 size={16}/></button>
        </div>)}
        <button type="button" className="button button-secondary parent-inline-button" onClick={() => patch('authorizedPickup', [...form.authorizedPickup, emptyPickup()])}><Plus size={16}/> Ajouter une personne</button>
      </div>}
    </article>
  </div>;

  const renderReview = () => <div className="parent-review-grid">
    <article><span>Situation familiale</span><strong>{familyStatusLabel(form.familyStatus)}</strong><small>Contact prioritaire : {preferredContactLabel()}</small></article>
    <article><span>Responsable légal 1</span><strong>{form.guardian1.fullName || '—'}</strong><small>{relationshipLabel(form.guardian1.relationship)} · {form.guardian1.phone || 'Téléphone non renseigné'}</small></article>
    <article><span>Responsable légal 2</span><strong>{form.guardian2.fullName || 'Non renseigné'}</strong><small>{form.guardian2.fullName ? `${relationshipLabel(form.guardian2.relationship)} · ${form.guardian2.phone || 'Téléphone non renseigné'}` : 'Facultatif'}</small></article>
    {form.children.map((child) => <article key={child.studentId}><span>{child.fullName}</span><strong>{child.className || 'Classe à confirmer'}</strong><small>Né(e) le {displayBirthDate(child)} à {child.birthPlace === 'Autre' ? (child.birthPlaceOther || '—') : (child.birthPlace || '—')} · Allergies : {yesNo(child.allergies)}</small></article>)}
    <article><span>Urgence</span><strong>{form.emergencyContact.fullName || 'Non renseigné'}</strong><small>{form.emergencyContact.fullName ? `${form.emergencyContact.relationship === 'Autre' ? (form.emergencyContact.relationshipOther || 'Autre') : (form.emergencyContact.relationship || '—')} · ${form.emergencyContact.phone || '—'}` : 'Facultatif'}</small></article>
    <article><span>Récupération</span><strong>{form.legalGuardiansOnlyPickup ? 'Responsables légaux uniquement' : `${form.authorizedPickup.length} personne(s) autorisée(s)`}</strong><small>Modifiable depuis Mon profil</small></article>
    {onboarding && <label className="parent-confirmation-row"><input type="checkbox" checked={form.confirmationAccepted} onChange={(e) => patch('confirmationAccepted', e.target.checked)}/><span>Je confirme que les informations communiquées sont exactes et à jour.</span></label>}
  </div>;

  const renderStep = () => {
    if (step === 0) return renderChildrenIdentity();
    if (step === 1) return renderFamily();
    if (step === 2) return renderGuardians();
    if (step === 3) return renderSchool();
    if (step === 4) return renderHealth();
    return renderReview();
  };

  return <section className={onboarding ? 'parent-onboarding-screen' : 'parent-profile-editor'}>
    <div className="parent-onboarding-shell">
      <header className="parent-onboarding-header">
        <div className="parent-onboarding-logo"><img src="/logo.png" alt="Le Défi"/><div><p className="eyebrow">{onboarding ? 'PREMIÈRE CONNEXION' : 'MON PROFIL'}</p><h1>{onboarding ? 'Complétez votre dossier parent' : 'Modifier mes informations'}</h1><p>{onboarding ? 'Ces informations permettent à l’école de mieux accompagner votre enfant et de vous contacter en cas de besoin.' : 'Mettez à jour les informations communiquées à l’école.'}</p></div></div>
        {onboarding && <div className="parent-onboarding-badge"><UserRoundCheck size={18}/><span>Étape obligatoire</span></div>}
      </header>

      <div className="parent-onboarding-progress" aria-label="Progression du formulaire">
        {steps.map((label, index) => <button type="button" key={label} className={`${index === step ? 'active' : ''} ${index < step ? 'done' : ''}`} onClick={() => !onboarding || index <= step ? setStep(index) : null}><span>{index < step ? <CheckCircle2 size={15}/> : index + 1}</span><b>{label}</b></button>)}
      </div>

      <div className="parent-onboarding-body">
        <div className="parent-onboarding-title"><span>Étape {step + 1} sur {steps.length}</span><h2>{steps[step]}</h2><p>{step === 0 ? 'Vérifiez et complétez les informations de chaque enfant. Le nom et la classe restent gérés par l’administration.' : step === 1 ? 'Précisez la situation familiale et l’organisation de la garde si nécessaire.' : step === 2 ? 'Renseignez les responsables légaux, puis choisissez le contact parent prioritaire.' : step === 3 ? 'Ajoutez les éléments scolaires utiles et les éventuels besoins pédagogiques.' : step === 4 ? 'Indiquez uniquement les informations nécessaires pour la sécurité et les urgences.' : 'Relisez les informations avant de les enregistrer.'}</p></div>
        {error && <div className="alert alert-error parent-onboarding-error">{error}</div>}
        {renderStep()}
      </div>

      <footer className="parent-onboarding-footer">
        <div>{!onboarding && onCancel && <button type="button" className="button button-secondary" onClick={onCancel}>Annuler</button>}</div>
        <div className="parent-onboarding-actions">
          {step > 0 && <button type="button" className="button button-secondary" onClick={() => { setError(''); setStep((current) => current - 1); }}><ArrowLeft size={16}/> Précédent</button>}
          {step < steps.length - 1 ? <button type="button" className="button button-primary" onClick={next}>Continuer <ArrowRight size={16}/></button> : <button type="button" className="button button-primary" onClick={save} disabled={saving}><Save size={16}/>{saving ? 'Enregistrement…' : onboarding ? 'Enregistrer et accéder à mon espace' : 'Enregistrer les modifications'}</button>}
        </div>
      </footer>
    </div>
  </section>;
}
